import React, { useState } from 'react';
import { Search, Clock, XCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { validateEmail } from '../../lib/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParticipantResult = ({ participant }) => (
  <div className={`waiver-result-item ${participant.is_valid ? 'valid' : 'expired'}`}>
    <div className="result-icon">
      {participant.is_valid ? (
        <CheckCircle2 size={20} className="icon-valid" />
      ) : (
        <XCircle size={20} className="icon-expired" />
      )}
    </div>
    <div className="result-details">
      <span className="result-name">
        {participant.relationship === 'self' ? 'You' : `${participant.participant_name} (minor)`}
      </span>
      <span className={`result-status ${participant.is_valid ? 'status-valid' : 'status-expired'}`}>
        {participant.is_valid ? (
          <>valid, signed {participant.signed_date} (good through {participant.expiration_date})</>
        ) : (
          <>EXPIRED, signed {participant.signed_date} (expired {participant.expiration_date})</>
        )}
      </span>
    </div>
    {!participant.is_valid && (
      <span className="result-action">please resign below</span>
    )}
  </div>
);

export const WaiverCheck = ({ onPrefillEmail, onGoToForm }) => {
  const [checkEmail, setCheckEmail] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkError, setCheckError] = useState('');
  const [hasChecked, setHasChecked] = useState(false);

  const handleWaiverCheck = async (e) => {
    e.preventDefault();

    if (!checkEmail.trim()) {
      setCheckError('Please enter your email address');
      return;
    }

    const emailValidation = validateEmail(checkEmail);
    if (!emailValidation.isValid) {
      setCheckError(emailValidation.error);
      return;
    }

    setCheckLoading(true);
    setCheckError('');
    setCheckResult(null);

    const email = checkEmail.trim().toLowerCase();

    try {
      const response = await axios.post(`${BACKEND_URL}/api/waiver/check`, { email });
      setCheckResult(response.data);
      setHasChecked(true);

      // Pre-fill form email when a re-sign or new signature is needed
      if (!response.data.found || response.data.participants.some(p => !p.is_valid)) {
        onPrefillEmail(email);
      }

      setTimeout(() => {
        const resultsEl = document.getElementById('waiver-check-results');
        if (resultsEl) {
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);

    } catch (err) {
      console.error('Waiver check error:', err);
      setCheckError('Unable to check waiver status. Please try again or proceed to sign below.');
      setHasChecked(true);
    } finally {
      setCheckLoading(false);
    }
  };

  return (
    <div className="waiver-check-section" data-testid="waiver-check-section">
      <div className="waiver-check-card">
        <div className="waiver-check-header">
          <Search size={24} />
          <h3>Already signed a waiver with us?</h3>
        </div>
        <p className="waiver-check-description">Check here first to see if your waiver is still valid.</p>

        <form onSubmit={handleWaiverCheck} className="waiver-check-form">
          <div className="waiver-check-input-row">
            <Input
              type="email"
              placeholder="Enter your email address"
              value={checkEmail}
              onChange={(e) => {
                setCheckEmail(e.target.value);
                setCheckError('');
              }}
              className="waiver-check-input"
              data-testid="waiver-check-email"
            />
            <Button
              type="submit"
              disabled={checkLoading}
              className="waiver-check-btn"
              data-testid="waiver-check-btn"
            >
              {checkLoading ? 'Checking...' : 'Check'}
            </Button>
          </div>
          {checkError && (
            <p className="waiver-check-error">{checkError}</p>
          )}
        </form>

        {hasChecked && (
          <div id="waiver-check-results" className="waiver-check-results">
            {checkResult?.found && checkResult.participants.length > 0 ? (
              <>
                <div className="waiver-results-list">
                  {checkResult.participants.map((participant) => (
                    <ParticipantResult
                      key={`${participant.participant_name}-${participant.signed_date}`}
                      participant={participant}
                    />
                  ))}
                </div>

                <div className="waiver-check-cta">
                  <p>
                    {checkResult.participants.some(p => !p.is_valid)
                      ? "Need to resign an expired waiver or sign for someone new? Complete the waiver below — it only takes a minute."
                      : "Need to sign for someone not listed above? Complete the waiver below — it only takes a minute."
                    }
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onGoToForm}
                    className="waiver-goto-form-btn"
                  >
                    Go to Waiver Form
                  </Button>
                </div>
              </>
            ) : (
              <div className="waiver-no-results">
                <Clock size={20} />
                <p>No waiver on file for this email. Complete the waiver below — it only takes a minute.</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onGoToForm}
                  className="waiver-goto-form-btn"
                >
                  Go to Waiver Form
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
